/**
 * WorkerLayout — simplified layout for role=worker users.
 *
 * Follows the same design language as AdminLayout: compact sidebar
 * with section dividers, 1px borders, no shadows, consistent spacing.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, IconButton, Tooltip, AppBar, Toolbar,
  Badge, Popover, Stack, Chip, Link as MuiLink,
 Menu, MenuItem, Divider } from '@mui/material';
import {
  AccountCircleOutlined,
  LogoutOutlined,
  NotificationsOutlined,
  OpenInNewOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { GUTTER } from '../../theme/shellMetrics';
import { useRoleProfile } from '../../hooks/useRoleProfile';
import { surfaceLicensed } from '../../auth/workerSurfaceLicence';
import { NAV_ICON } from './workerNavIcons';
import OrgSwitchToast from '../../components/navigation/OrgSwitchToast';
import { LanguagePicker } from '../../components/common/LanguagePicker';
import ChangePasswordInlineDialog from '../../components/security/ChangePasswordDialog';
import { activeLocale } from '../../utils/datetime';

import { localizeProcessNotificationArgs } from '../Processes/processDisplay';

const DRAWER_WIDTH = 220;
// Single source of truth for the header band height so the sidebar brand
// header (left) and the top app bar (right) are pixel-identical and their
// bottom borders form ONE continuous line — no notch at the seam (owner
// 2026-06-28: 左右高度不一致/缺口).
const HEADER_HEIGHT = 56;

interface NavItem {
  labelKey: string;
  /**
   * The page's OWN title key, when the sidebar should read it instead of a separate label.
   *
   * Owner 2026-09-04: 侧边栏标题的，一个是 My Tasks，一个只有 Approvals，但是页面中又显示得是
   * My Approvals，这种细节你要举一反三. An audit of all fifteen entries found THREE drifts, not
   * one: Leave / My Leave, Face reviews / My face reviews, Approvals / My Approvals.
   *
   * The cause is one name with two sources (svdy-rules §10.5b), so the fix is to delete one of
   * them: where `titleKey` is set, the sidebar renders the string the PAGE renders, and the two
   * cannot disagree again. `labelKey` stays as the fallback for entries whose page has no h6
   * title of its own (Dashboard, Profile, Org chart, Security).
   */
  titleKey?: string;
  icon: React.ReactElement;
  /** Navigate to this route. Mutually exclusive with `action`. */
  path?: string;
  /** Non-navigation action (opens a dialog instead of routing). */
  action?: 'changePassword';
  /** Optional pending-count badge. */
  badge?: number;
  /** Where a BADGE click goes. A badge is a count, so it opens the list that count describes, while the
   *  entry itself still opens the page's own default view. Only set where `badge` is. */
  badgePath?: string;
}

interface NavSection {
  /** i18n key under worker:layout for the small-caps header. Omit on the
   *  first (Overview) section so Dashboard sits flush. */
  labelKey?: string;
  items: NavItem[];
}

// Compliance footer links (owner 2026-06-26) — open svdy.com legal pages.
const COMPLIANCE_LINKS: { labelKey: string; href: string }[] = [
  { labelKey: 'compPrivacy', href: 'https://svdy.com/privacy' },
  { labelKey: 'compTerms', href: 'https://svdy.com/terms' },
  { labelKey: 'compSecurity', href: 'https://svdy.com/security' },
  { labelKey: 'compCompliance', href: 'https://svdy.com/compliance' },
];

/**
 * Build the worker-portal sidebar IA from the resolved role — mirrors the
 * mobile app's grouping (svdy-mobile activity hub + profile):
 *   Overview · Attendance (QuTime) · My requests · To-do (PureOA) ·
 *   Team (oversight) · Organization · Account.
 *
 * License + oversight gated so each persona sees only what's relevant:
 *   - Attendance surfaces require qutime.
 *   - To-do (Tasks/Approvals) + Office requests require pureoa.
 *   - Team / Attendance-approvals require oversight (team lead OR admin),
 *     gated by the SAME backend signal mobile uses (manager-overview), NOT
 *     the coarse role_code — so a role='worker' team lead sees them too.
 */

export function buildNavSections(opts: {
  hasQutime: boolean;
  hasPureoa: boolean;
  oversight: 'none' | 'team' | 'org';
  canReview: boolean;
  /** The permission OR being a routed approver — what the To-do entry gates on (2026-09-07). */
  attendanceReviewer: boolean;
  /** The same, for the FACE queue — counted separately so an empty one does not appear (2026-09-08). */
  faceReviewer: boolean;
  tracked: boolean;
  pendingAttendance: number;
  /** Pending FACE reviews routed to this caller. Its own count, because its row has its own gate. */
  pendingFace: number;
}): NavSection[] {
  // `canReview` is deliberately NOT destructured any more (2026-09-08): both queues now gate on their own
  // ROUTED authority, so the org-wide permission decides nothing in this file. It stays on the opts type
  // because callers still pass it and other surfaces read it.
  const { hasQutime, hasPureoa, oversight, attendanceReviewer, faceReviewer, tracked, pendingAttendance, pendingFace } = opts;
  /**
   * The LICENCE half of every guard below, from the one place it is declared
   * (`src/auth/workerSurfaceLicence.ts`). The route boundaries in App.tsx read the same map, so a
   * sidebar entry and its route can no longer disagree — which they did for three surfaces until
   * 2026-09-04.
   *
   * Role conditions (`oversight`, `canReview`, `tracked`) stay INLINE and are deliberately not in that
   * map: they are not licences, they differ per surface for reasons the map has no vocabulary for, and
   * folding them in would turn a one-concern lookup into a nav config.
   */
  const lic = (path: string): boolean => surfaceLicensed(path, { qutime: hasQutime, pureoa: hasPureoa });
  const sections: NavSection[] = [
    { items: [{ labelKey: 'navDashboard', icon: NAV_ICON['/me/dashboard'], path: '/me/dashboard' }] },
  ];

  // ATTENDANCE — the worker's own record. `My Face Checks` belongs HERE, not under Requests:
  // it is the OUTCOME of their own clock-ins, the same family as Time Entries, and it is not
  // something they submitted. Moved 2026-09-04 when the owner compared the two clients side by
  // side; deriving membership from what the surface IS is svdy-ui §20.
  const attendance: NavItem[] = [];
  if (lic('/me/schedule')) attendance.push({ labelKey: 'navSchedule', icon: NAV_ICON['/me/schedule'], path: '/me/schedule', titleKey: 'schedule.title' });
  if (lic('/me/time-entries')) attendance.push({ labelKey: 'navTimeEntries', icon: NAV_ICON['/me/time-entries'], path: '/me/time-entries', titleKey: 'timeEntries.title' });
  if (lic('/me/leave')) attendance.push({ labelKey: 'navLeave', icon: NAV_ICON['/me/leave'], path: '/me/leave', titleKey: 'leave.title' });
  if (lic('/me/holidays')) attendance.push({ labelKey: 'navHolidays', icon: NAV_ICON['/me/holidays'], path: '/me/holidays', titleKey: 'holidays.title' });
  if (lic('/me/clock-in-reviews')) attendance.push({ labelKey: 'navFaceReviews', icon: NAV_ICON['/me/clock-in-reviews'], path: '/me/clock-in-reviews', titleKey: 'clockInReviews.title' });
  if (attendance.length) sections.push({ labelKey: 'secAttendance', items: attendance });

  // TO-DO comes FIRST, and REQUESTS second — reversed 2026-09-04.
  //
  // The phone puts the waiting-on-me group above the submitted-by-me group in both of its hubs, so
  // this was 2 surfaces against 1. It is also the better default independent of the vote: what is
  // blocking OTHER people outranks what other people are doing for me, and the badges live here.
  //
  // MEMBERSHIP is derived from what each row IS, which resolved a disagreement where BOTH clients
  // were partly wrong:
  //   things I submitted        -> Requests   (attendance requests, process requests)
  //   things waiting on ME      -> To-do      (tasks I complete + three queues I decide)
  //   outcomes of my own punches-> Attendance (my face checks — moved up, see above)
  // The phone had `Process Tasks` under `My Requests`, which misdescribes it: nobody requested it,
  // it is a step assigned to me. The web had it here already and that placement is the correct one.
  const todo: NavItem[] = [];
  if (lic('/me/tasks')) todo.push({ labelKey: 'navTasks', icon: NAV_ICON['/me/tasks'], path: '/me/tasks', titleKey: 'tasks.title' });
  if (lic('/me/approvals')) {
    todo.push({ labelKey: 'navApprovals', icon: NAV_ICON['/me/approvals'], path: '/me/approvals', titleKey: 'approvals.title' });
  }
  if (lic('/me/attendance-approvals') && attendanceReviewer) {
    todo.push({ labelKey: 'navAttendanceApprovals', icon: NAV_ICON['/me/attendance-approvals'], path: '/me/attendance-approvals', badgePath: '/me/attendance-approvals?status=pending', badge: pendingAttendance || undefined, titleKey: 'attendanceApprovals.title' });
  }
  // FACE APPROVALS — the queue the phone has had and the web worker portal did not, so a team lead
  // could clear clock-in photos on their phone and not at their desk.
  //
  // The premise was checked rather than assumed, because the phone's own file comment claims this
  // surface is "Admin-only: the backend RBAC-gates /attendance/approvals". It is not: the seeded
  // `manager` role carries BOTH `attendance.approvals.read` and `.manage`
  // (app/core/permissions.py), and `manager` is precisely the role that stays in the worker portal
  // — `isAdminTierRole` covers super_admin/admin/hr_manager/owner/org_admin, not manager. An
  // admin-tier reviewer would be redirected to /dashboard and use the Attendance module instead.
  //
  // ── SIBLING, NOT CHILD (owner, production, 2026-09-08) ─────────────────────────────────────────
  //
  // This entry used to live INSIDE the `attendanceReviewer` block above, which made attendance
  // authority a precondition for seeing the face queue. The two are INDEPENDENT booleans on the
  // server —
  //     attendance_reviewer = can_review or attendance > 0
  //     face_reviewer       = can_review or face      > 0
  // — so `faceReviewer && !attendanceReviewer` is reachable and is the ordinary case it was reported
  // in: a `worker`-role team lead whose report has a flagged clock-in photo and NO pending leave or
  // missed-punch request. `attendance` is 0, so the outer gate closed, so the row it contained never
  // rendered — while `WorkerDashboard`'s To-do card, which gates on `face_approvals > 0` and never
  // had the nesting, offered the very row whose sidebar entry did not exist. Two gates for one
  // surface, disagreeing (svdy-rules §10.5b).
  //
  // Nesting is what made it invisible to review AND to the tests: the string
  // `if (lic('/me/face-reviews') && faceReviewer)` was present and correct, so every assertion that
  // searched for it passed (svdy-ui §16 — presence is not reachability).
  if (lic('/me/face-reviews') && faceReviewer) {
    todo.push({ labelKey: 'navFaceApprovals', icon: NAV_ICON['/me/face-reviews'], path: '/me/face-reviews', badgePath: '/me/face-reviews?status=pending', badge: pendingFace || undefined, titleKey: 'faceReviews.title' });
  }
  if (todo.length) sections.push({ labelKey: 'secTodo', items: todo });

  // REQUESTS — what I submitted and am tracking. Two entries, one per product, split from a tabbed
  // hub by owner decision 2026-09-04 (option B) so the web matches the phone row for row.
  const myRequests: NavItem[] = [];
  if (lic('/me/attendance-requests')) myRequests.push({ labelKey: 'navAttendanceRequests', icon: NAV_ICON['/me/attendance-requests'], path: '/me/attendance-requests', titleKey: 'attendanceRequests.title' });
  if (lic('/me/requests')) myRequests.push({ labelKey: 'navRequests', icon: NAV_ICON['/me/requests'], path: '/me/requests', titleKey: 'requests.title' });
  if (myRequests.length) sections.push({ labelKey: 'secMyRequests', items: myRequests });

  // Team — oversight overview (team lead / admin). Attendance approvals are now
  // part of the unified Approvals entry above, not a separate Team item.
  // NO licence clause: `/me/team` is a direct-reports ROSTER, which is employment data the backend
  // serves to any licence (`list_team`: *"My direct reports" is EMPLOYMENT, not attendance*). Gating
  // the whole page on QuTime kept a PureOA-only manager away from a roster the API would give them.
  // The page gates its own punch columns and summary strip on `hasQutime` instead — the licence
  // follows the CONTENT, not the page.
  if (lic('/me/team') && oversight !== 'none') {
    sections.push({
      labelKey: 'secTeam',
      items: [
        { labelKey: 'navTeam', icon: NAV_ICON['/me/team'], path: '/me/team', titleKey: 'team.title' },
      ],
    });
  }

  sections.push({
    labelKey: 'secOrganization',
    items: [
      { labelKey: 'navOrganization', icon: NAV_ICON['/me/organization'], path: '/me/organization' },
      { labelKey: 'navOrgChart', icon: NAV_ICON['/me/org-chart'], path: '/me/org-chart' },
    ],
  });

  // Account — never product-gated as a section (everyone has a profile), but the
  // face entry inside it is: `/me/face-template` is the enrolment screen for a
  // face-verified punch, which only QuTime performs.
  const account: NavItem[] = [
    { labelKey: 'navProfile', icon: NAV_ICON['/me/profile'], path: '/me/profile' },
  ];
  // FACE SETUP is gated on `attendance_tracked` as well as the licence — the one place in this
  // sidebar where that flag decides anything.
  //
  // Measured 2026-09-04 rather than assumed: `attendance_tracked` has ZERO rejection-class uses in
  // the backend (no HTTPException, no permission gate). All 15 read sites are reporting-population
  // filters plus a purge of summary rows on toggle-off, and neither the punch endpoint nor any
  // request-creation path checks it. So it means "is this person's attendance CALCULATED", not
  // "may this person use attendance features", and it is NOT a general licence-shaped gate.
  //
  // It gates this entry because face enrolment exists only to serve a face-verified punch. Asking
  // someone who never punches to hand over biometrics collects GDPR Art. 9 special-category data
  // with no purpose — a defect regardless of how much sidebar room it costs.
  //
  // It deliberately does NOT gate Schedule / Time Entries / Leave / Holidays here, even though the
  // phone hides those. That is not drift: the phone has FOUR tab slots, where an empty tab is
  // expensive, and this sidebar has sixteen entries, where an honest empty state is cheap. Hiding
  // them would also cost something real — a worker mis-flagged as untracked would lose sight of
  // their own punches.
  if (lic('/me/face-template') && tracked) {
    account.push({ labelKey: 'navFaceMatching', icon: NAV_ICON['/me/face-template'], path: '/me/face-template', titleKey: 'faceTemplate.title' });
  }
  sections.push({ labelKey: 'secAccount', items: account });

  return sections;
}

const WorkerLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const { user, signOut } = useAuth();

  const displayName = user?.full_name || user?.email?.split('@')[0] || 'Worker';
  const initials = displayName.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

  const { role } = useRoleProfile();

  // Role-driven sidebar IA (mirrors mobile). Until the role resolves NOTHING
  // product-specific is offered.
  //
  // `hasQutime` defaulted to `true` here — "so the first paint isn't empty" — which
  // made the first paint of a PureOA-only worker's portal a QuTime portal:
  // Schedule, Time entries, Leave, Holidays, Attendance requests, Face reviews and
  // Account → Face matching, all removed a moment later. Face matching is the one
  // that is not merely cosmetic: it invites the worker to enrol biometric data
  // their employer has no lawful basis to process (GDPR Art. 9).
  //
  // The empty first paint the old default was avoiding is not what happens — the
  // Dashboard, Organization and Account sections are unlicensed and always render;
  // only the product sections wait for the answer.
  const visibleSections = buildNavSections({
    hasQutime: role ? role.hasQutime : false,
    hasPureoa: role ? role.hasPureoa : false,
    oversight: role?.oversight ?? 'none',
    canReview: role?.canReview ?? false,
    attendanceReviewer: role?.attendanceReviewer ?? false,
    faceReviewer: role?.faceReviewer ?? false,
    // `attendance_tracked` — see the Account section for what it gates and why it gates only that.
    tracked: role ? role.tracked : true,
    pendingAttendance: role?.pending.attendance ?? 0,
    pendingFace: role?.pending.faceReviews ?? 0,
  });

  // Change-password dialog (Account → Sign-in & security). Shared component.
  const [changePwdOpen, setChangePwdOpen] = React.useState(false);

  // Notifications
  const [notifications, setNotifications] = React.useState<{ id: string; title: string; body: string; time: string; read: boolean; link: string | null }[]>([]);
  const [bellAnchor, setBellAnchor] = React.useState<HTMLElement | null>(null);
  const unreadCount = notifications.filter(n => !n.read).length;

  React.useEffect(() => {
    const load = () => {
      import('../../api/client').then(({ default: api }) => {
        api.get('/v1/notifications').then((r: any) => {
          setNotifications((r.data ?? []).map((n: any) => ({
            // Same rendering as the admin bell (TopNav): the catalog key in the reader's language, with a
            // built-in process's {{title}} localized by provenance; the stored English is the fallback.
            id: n.id,
            title: n.title_key ? t(n.title_key, { ...localizeProcessNotificationArgs(t, n.i18n_args), defaultValue: n.title }) : n.title,
            body: n.body_key ? t(n.body_key, { ...localizeProcessNotificationArgs(t, n.i18n_args), defaultValue: n.body ?? '' }) : (n.body ?? ''),
            time: n.created_at ?? '', read: n.read ?? false, link: n.link ?? null,
          })));
        }).catch(() => {});
      });
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
    // `t` so a language switch re-renders the bell in the new language instead of on the next poll.
  }, [t]);

  const markAllRead = () => {
    import('../../api/client').then(({ default: api }) => {
      api.post('/v1/notifications/mark-all-read').then(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }).catch(() => {});
    });
  };

  // BUG-6 fix (2026-07-07): a handful of server-side notification links target
  // MOBILE-ONLY capabilities (clock-in is a phone geofence+face action, not a
  // web page — svdy-rules §16 product boundary) and have no web route. Without
  // this map, the click falls through to the global catch-all `RoleRedirect`,
  // which silently bounces to a role default page — not a crash, but the
  // click's intent is lost. Map the small, known set to the closest
  // meaningful web page (the worker's clock-in status IS shown on the
  // dashboard, read-only) instead of relying on the generic 404 fallback.
  const WEB_LINK_FALLBACK: Record<string, string> = {
    '/clock-in': '/me/dashboard',
  };

  // Click on a single notification: mark read + (P2.7) deep-link
  // navigate. Click without link just dismisses + marks read.
  const markReadAndOpen = (id: string, link: string | null) => {
    import('../../api/client').then(({ default: api }) => {
      api.post(`/v1/notifications/${id}/read`).then(() => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      }).catch(() => {});
    });
    setBellAnchor(null);
    if (link) {
      navigate(WEB_LINK_FALLBACK[link] ?? link);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Privacy audit Wave 5 polish 2026-05-22 — post-org-switch toast.
          Reads sessionStorage SWITCH_TOAST_KEY set by OrgSwitcher.handlePick
          before reload. Auto-clears on first render. */}
      <OrgSwitchToast />
      {/* Single full-width header (clipped-drawer pattern) — ONE element spans
          the entire top, so its bottom border is a single continuous line and
          the sidebar/topbar seam notch is structurally impossible. zIndex sits
          ABOVE the permanent drawer so the bar clips it. */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        {/* Definite Toolbar height (not height:100% — that collapsed in the
            AppBar's flex column and pushed the right-zone content to the top,
            off-center). A fixed height + the zones' alignItems:center keeps
            "Worker Portal" + the controls vertically centered in the bar. */}
        <Toolbar disableGutters sx={{ minHeight: HEADER_HEIGHT, height: HEADER_HEIGHT, p: 0 }}>
          {/* Left identity zone — exactly the sidebar width + a matching right
              border, so it reads as the sidebar's header and the vertical
              divider runs continuously top-to-bottom. */}
          <Box sx={{ width: DRAWER_WIDTH, flexShrink: 0, height: '100%', display: 'flex', alignItems: 'center', px: 2, borderRight: '1px solid', borderColor: 'divider' }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem', mr: 1.25 }}>
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t('worker:layout.employee')}
              </Typography>
            </Box>
          </Box>
          {/* Right zone — portal title + controls. */}
          <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'center', px: `${GUTTER}px` }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('worker:layout.workerPortal')}
            </Typography>
            <Box sx={{ flex: 1 }} />
          <Box sx={{ mr: 1 }}>
            <LanguagePicker />
          </Box>
          <Tooltip title={t('worker:layout.notifications', { defaultValue: 'Notifications' })}>
            <IconButton size="small" onClick={(e) => setBellAnchor(e.currentTarget)} sx={{ mr: 1.5 }}>
              <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                <NotificationsOutlined fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Popover
            open={!!bellAnchor}
            anchorEl={bellAnchor}
            onClose={() => setBellAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 320, maxHeight: 400, borderRadius: 2 } }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{t('worker:layout.notifications', { defaultValue: 'Notifications' })}</Typography>
              {unreadCount > 0 && (
                <Typography onClick={markAllRead} sx={{ fontSize: '0.75rem', color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}>
                  {t('worker:layout.markAllRead')}
                </Typography>
              )}
            </Box>
            {notifications.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{t('worker:layout.noNotifications', { defaultValue: 'No notifications' })}</Typography>
              </Box>
            ) : (
              <Stack sx={{ maxHeight: 320, overflow: 'auto' }}>
                {notifications.slice(0, 20).map((n) => (
                  <Box
                    key={n.id}
                    onClick={() => markReadAndOpen(n.id, n.link)}
                    sx={{
                      px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
                      bgcolor: n.read ? 'transparent' : 'rgba(0,113,227,0.04)',
                      cursor: n.link ? 'pointer' : 'default',
                      transition: 'background-color 80ms ease',
                      '&:hover': n.link ? {
                        bgcolor: n.read ? 'rgba(0,0,0,0.02)' : 'rgba(0,113,227,0.08)',
                      } : undefined,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>{n.title}</Typography>
                    {n.body && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{n.body}</Typography>}
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
                      {n.time ? new Date(n.time).toLocaleString(activeLocale()) : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Popover>
          {/* One account menu instead of a row of bare icons.
              There were two (change password, sign out) and this needed a third
              for two-factor — three side-by-side icons is where svdy-ui §12.2
              says to collapse, and it also removes a shape mismatch: change
              password opens a dialog while security is a page, so two different
              affordances were serving the same "my account" concern.

              The 2FA entry is the substantive part. `/me/security` was a routed
              but completely UNREACHABLE page — nothing in this portal linked to
              it, and this shell renders no TopNav, so a worker could only get
              there by typing the URL. Meanwhile the org security settings page
              tells admins that "frontline workers can turn it on optionally",
              which on web they could not. Same unfulfillable-claim class as the
              annual-billing copy. */}
          <Tooltip title={t('worker:layout.account', { defaultValue: 'Account' })}>
            <IconButton
              size="small"
              onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
              sx={{ color: 'text.secondary' }}
              aria-label={t('worker:layout.account', { defaultValue: 'Account' })}
            >
              <AccountCircleOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={accountMenuAnchor}
            open={!!accountMenuAnchor}
            onClose={() => setAccountMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {/* Each item closes explicitly — svdy-ui §12.6. */}
            {/* Change password is NOT a separate item — it lives on the
                Sign-in & security page below, next to two-factor. One action,
                one entry point, same as the admin shell's avatar menu. */}
            <MenuItem
              onClick={() => {
                setAccountMenuAnchor(null);
                navigate('/me/security');
              }}
            >
              <ShieldOutlined fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
              {t('worker:layout.signInSecurity', { defaultValue: 'Sign-in & security' })}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAccountMenuAnchor(null);
                signOut();
              }}
            >
              <LogoutOutlined fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
              {t('worker:layout.signOut', { defaultValue: 'Sign out' })}
            </MenuItem>
          </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
      >
        {/* Spacer = header height: the full-width header clips the drawer, so
            the nav starts BELOW it (classic clipped-drawer). The user identity
            now lives in the header's left zone. */}
        <Box sx={{ height: HEADER_HEIGHT, flexShrink: 0 }} />

        <List sx={{ px: 1, py: 1.5 }}>
          {visibleSections.map((section, sIdx) => (
            <Box key={section.labelKey ?? `section-${sIdx}`} sx={{ mb: sIdx < visibleSections.length - 1 ? 1.25 : 0 }}>
              {section.labelKey && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 1.5,
                    pt: 1.25,
                    pb: 0.5,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {t(`worker:layout.${section.labelKey}`)}
                </Typography>
              )}
              {section.items.map((item) => {
                const active = !!item.path && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
                const onClick = () => {
                  if (item.action === 'changePassword') setChangePwdOpen(true);
                  // A badge is a COUNT, so a click on a badged entry opens the list that badge counted
                  // rather than the page's default view (owner, production 2026-09-05). Unbadged entries
                  // keep the default, which is what a worker browsing their history wants.
                  else if (item.badge && item.badgePath) navigate(item.badgePath);
                  else if (item.path) navigate(item.path);
                };
                return (
                  <ListItemButton
                    key={item.path ?? item.labelKey}
                    onClick={onClick}
                    selected={active}
                    sx={{
                      borderRadius: 1,
                      mb: 0.25,
                      py: 0.75,
                      px: 1.25,
                      // The label and the badge are flex SIBLINGS, so the row needs a gap or the count
                      // sits against the last letter (owner, production, 2026-09-08: `Attendance
                      // Approvals` + `2` rendered as "Approvals2"). It looked state-dependent — the
                      // SELECTED entry wrapped cleanly and the unselected one collided — but selection
                      // only changes `fontWeight` to 600, which widens the text enough to wrap on its
                      // own. The wrap was never a design; it was the symptom that appeared once the
                      // label outgrew the space it was silently overlapping.
                      gap: 1,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30, color: active ? 'primary.contrastText' : 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.titleKey ? t(`worker:${item.titleKey}`) : t(`worker:layout.${item.labelKey}`)}
                      primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 400 }}
                      // `minWidth: 0` lets the label WRAP instead of overflowing its flex basis, which is
                      // what pushed it under the badge. Wrapping is the intended behaviour for every
                      // entry and every state (owner 2026-09-08: 所有的页面都要换行) — a two-line label is
                      // correct, a count touching a word is not. `my: 0` keeps the row's own vertical
                      // rhythm when the label takes two lines.
                      sx={{ minWidth: 0, my: 0 }}
                    />
                    {!!item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color="error"
                        // `flexShrink: 0` — a count must never be compressed to make room for the label
                        // it annotates; the label wraps instead.
                        sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </Box>
          ))}

          {/* Compliance footer — svdy.com legal pages (owner 2026-06-26). */}
          <Box sx={{ px: 1.5, pt: 2, pb: 1, mt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={0.25}>
              {COMPLIANCE_LINKS.map((c) => (
                <MuiLink
                  key={c.labelKey}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontSize: '0.7rem', color: 'text.secondary',
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {t(`worker:layout.${c.labelKey}`)}
                  <OpenInNewOutlined sx={{ fontSize: 11 }} />
                </MuiLink>
              ))}
            </Stack>
          </Box>
        </List>
      </Drawer>

      {/* Main */}
      {/* The worker portal is its own shell, so it supplies the gutter for its own
          pages — the same token as the admin shell, not a second number. */}
      <Box component="main" sx={{ flexGrow: 1, pt: `${HEADER_HEIGHT}px`, minWidth: 0, px: `${GUTTER}px` }}>
        <Outlet />
      </Box>

      {/* Account → Sign-in & security: change password (shared dialog). */}
      <ChangePasswordInlineDialog open={changePwdOpen} onClose={() => setChangePwdOpen(false)} />
    </Box>
  );
};

export default WorkerLayout;
